import { ForwardedRef, forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { GoogleMap, MarkerF, CircleF, InfoWindowF } from '@react-google-maps/api';
import useQueryGetMe from '@/api/auth/getMe.ts';
import { DeviceStatus } from '@/api/user/getUsers.ts';
import { WanderingStatus } from '@/util/location.ts';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';
import { IMarker, IMarkerInfo, MarketTYPE } from '@/pages/dashboard/DashboardMapDefault.tsx';
import { IReqGetUsersBbox, IUserBbox } from '@/api/user/getUsersBbox.ts';

interface IProps {
    className?: string;
    users?: IUserBbox[];
    onBoundsChange?: (bounds: IReqGetUsersBbox) => void;
}

const CACHE__ZOOM_LEVEL = 'dashboard_map__zoom';
const CACHE__CENTER_X = 'dashboard_map__center__longitude';
const CACHE__CENTER_Y = 'dashboard_map__center__latitude';

const DashboardMapForCareManager = forwardRef(({ className, users, onBoundsChange }: IProps, ref: ForwardedRef<any>) => {
    const [map, setMap] = useState<any>(null);
    const [userInfo, setUserInfo] = useState<IMarkerInfo>();
    const { data: me } = useQueryGetMe();

    useImperativeHandle(ref, () => ({
        setLocation(latitude: number, longitude: number, zoom?: number) {
            // 위도/경도 값이 유효한 숫자인지 확인
            if (
                typeof latitude === 'number' && 
                typeof longitude === 'number' && 
                !isNaN(latitude) && 
                !isNaN(longitude) &&
                isFinite(latitude) && 
                isFinite(longitude)
            ) {
                map?.panTo({ lat: latitude, lng: longitude });
                if (zoom && typeof zoom === 'number' && !isNaN(zoom) && isFinite(zoom)) {
                    map?.setZoom(zoom);
                }
            } else {
                console.warn('Invalid coordinates provided to setLocation:', { latitude, longitude, zoom });
            }
        }
    }));

    const handleIdle = () => {
        if (!map) return;
        const mapBounds = map.getBounds();
        if (mapBounds && onBoundsChange) {
            const ne = mapBounds.getNorthEast();
            const sw = mapBounds.getSouthWest();
            onBoundsChange({
                startLat: sw.lat(),
                endLat: ne.lat(),
                startLng: sw.lng(),
                endLng: ne.lng()
            });
        }
    };

    const openUserInfo = (info?: IMarker) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};

            // 마커 클릭 시 줌인
            if (map && latitude && longitude) {
                map.panTo({ lat: latitude, lng: longitude });
                map.setZoom(17); // 줌 레벨 17로 확대
            }

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { location: { lat: latitude, lng: longitude } },
                (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        const infoWithAddress: IMarkerInfo = {
                            ...info!,
                            address: results[0].formatted_address
                        };

                        setUserInfo(infoWithAddress);
                    } else {
                        const infoWithoutAddress: IMarkerInfo = {
                            ...info!,
                            address: 'Geocoding failed',
                        };

                        setUserInfo(infoWithoutAddress);
                    }
                }
            );
        }
    };


    const markers: IMarker[] = useMemo(() => {
        const filtered = users?.filter(item => item?.location) || [];

        // 군집화 제거 - 모든 사용자를 개별 마커로 표시
        const markers: IMarker[] = filtered.map((user) => {
            // API에서 이미 배회 상태를 계산하여 제공함
            return {
                id: user.id,
                deviceStatus: user.deviceStatus,
                wanderingStatus: user.wanderingStatus || WanderingStatus.UNKNOWN,
                distanceFromHome: user.distanceFromHome,
                location: {
                    latitude: user.location?.latitude,
                    longitude: user.location?.longitude,
                    accuracy: user.location?.accuracy,
                    timestamp: user.location?.timestamp
                },
                type: MarketTYPE.SINGLE,
                users: [user]
            };
        });

        // 우선순위 정렬: 배회 이탈 > 긴급 > 온라인 > 충전 > 오프라인
        markers.sort((a, b) => {
            const priority = {
                [WanderingStatus.WANDERING]: 5,
                [DeviceStatus.EMERGENCY]: 4,
                [DeviceStatus.ONLINE]: 3,
                [DeviceStatus.CHARGING]: 2,
                [DeviceStatus.OFFLINE]: 1,
                [WanderingStatus.SAFE]: 1,
                [WanderingStatus.UNKNOWN]: 0,
            };

            const priorityA = priority[a.wanderingStatus ?? WanderingStatus.UNKNOWN] || 
                             priority[a.deviceStatus ?? DeviceStatus.OFFLINE] || 0;
            const priorityB = priority[b.wanderingStatus ?? WanderingStatus.UNKNOWN] || 
                             priority[b.deviceStatus ?? DeviceStatus.OFFLINE] || 0;

            return priorityB - priorityA;
        });

        return markers;
    }, [users]);

    return (
        <div className={`${className}`}>
            <div style={{ width: '100%', height: '100%' }}>
                {me && (
                    <GoogleMap
                        onLoad={(mapInstance) => setMap(mapInstance)}
                        center={{
                            lat: Number(localStorage.getItem(CACHE__CENTER_Y)) || me?.latitude || 37.5759,
                            lng: Number(localStorage.getItem(CACHE__CENTER_X)) || me?.longitude || 126.9768
                        }}
                        zoom={Number(localStorage.getItem(CACHE__ZOOM_LEVEL)) || 18}
                        onZoomChanged={() => {
                            localStorage.setItem(CACHE__ZOOM_LEVEL, map?.getZoom()?.toString() || '18');
                        }}
                        onCenterChanged={() => {
                            const center = map?.getCenter();
                            if (center) {
                                localStorage.setItem(CACHE__CENTER_X, center.lng().toString());
                                localStorage.setItem(CACHE__CENTER_Y, center.lat().toString());
                            }
                        }}
                        onIdle={handleIdle}
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                    >
                        {markers?.map(item => (
                            <MarkerF
                                key={`marker_${item?.id}`}
                                position={{ lat: item?.location?.latitude || 0, lng: item?.location?.longitude || 0 }}
                                icon={{
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                        (() => {
                                            let color: string = '#2966F4';
                                            let shouldBlink = false;
                                            
                                            // 1순위: 배회 이탈 (빨강, 깜빡임)
                                            if (item?.wanderingStatus === WanderingStatus.WANDERING) {
                                                color = '#FF003D';
                                                shouldBlink = true;
                                            }
                                            // 2순위: 긴급 (주황)
                                            else if (item?.deviceStatus === DeviceStatus.EMERGENCY) {
                                                color = '#FF6B00';
                                            }
                                            // 3순위: 온라인 (파랑)
                                            else if (item?.deviceStatus === DeviceStatus.ONLINE) {
                                                color = '#2966F4';
                                            }
                                            // 4순위: 충전중 (연두)
                                            else if (item?.deviceStatus === DeviceStatus.CHARGING) {
                                                color = '#ADFF45';
                                            }
                                            // 기타: 회색
                                            else {
                                                color = '#BABABA';
                                            }

                                            const userName = item?.users?.[0]?.name || '';
                                            const displayName = userName.length > 3 ? userName.substring(0, 3) : userName;

                                            return `
                                                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="50" viewBox="0 0 80 50">
                                                    <defs>
                                                        <filter id="shadow_${item?.id}" x="-50%" y="-50%" width="200%" height="200%">
                                                            <feDropShadow dx="0" dy="0" stdDeviation="4" flood-color="${color}"/>
                                                        </filter>
                                                    </defs>
                                                    <circle cx="40" cy="25" r="18" fill="${color}" stroke="white" stroke-width="3" filter="url(#shadow_${item?.id})">
                                                        ${shouldBlink ? '<animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite"/>' : ''}
                                                    </circle>
                                                    <text x="40" y="30" fill="white" font-size="12" font-family="Arial, sans-serif" text-anchor="middle" font-weight="bold">
                                                        ${displayName}
                                                    </text>
                                                </svg>
                                            `;
                                        })() as string
                                    )}`
                                }}
                                onClick={() => openUserInfo(item)}
                            />
                        ))}

                        {userInfo && (
                            <CircleF
                                center={{ lat: userInfo?.location?.latitude || 0, lng: userInfo?.location?.longitude || 0 }}
                                radius={100}
                                options={{
                                    fillColor: '#78bfff66',
                                    strokeColor: '#2966F4',
                                    strokeWeight: 2,
                                }}
                            />
                        )}
                        {userInfo && (
                            <InfoWindowF
                                position={{ lat: userInfo.location?.latitude || 0, lng: userInfo.location?.longitude || 0 }}
                                onCloseClick={() => setUserInfo(undefined)}
                            >
                                <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                    <MarkerWindow info={userInfo} isMasking={me?.email === 'dbd_cm@clomental.com'} />
                                </div>
                            </InfoWindowF>
                        )}
                    </GoogleMap>
                )}
            </div>
        </div>
    );
});

export default DashboardMapForCareManager;
